import { GROK_WORKER_URL } from '../../js/config.js';
import { storageManager } from '../../core/storage/index.js';
import { fetchCache } from './fetchCache.js';
import {
  getScenarioDescriptionFromHangoutConfig,
  getScenarioIdFromHangoutConfig,
  getScenarioNameFromHangoutConfig
} from '../hangout/shared/scenario-config.js';
import { isPlaceholderCharacterName } from '../rankings/components/character-name-utils.js';
import { normalizeRemoteConversationMessages } from '../hangout/shared/remote-message-normalize.js';
import { militaryToTimeOfDaySlot } from '../hangout/shared/message-tree.js';

// Storage utility with linear conversation support (single branch per conversation)
// Character management uses allCharacters array (isActive property removed)

const STORAGE_KEYS = {
  // User
  TOKEN: 'auth_token',
  USER: 'userData',
  SELECTED_LANGUAGE: 'displayLanguage',
  USER_INVENTORY: 'userInventory',
  COINS: 'coins',

  // Character
  SELECTED_CHARACTER_STATS: 'selectedCharacterForStats',
  SELECTED_CHARACTER_GIFT: 'selectedCharacterForGift',
  OPEN_CHAT_WITH: 'openChatWith',

  CHARACTER_DATA: 'characterData',

  CHARACTER_POSITIONS: 'characterPositions',
  CHARACTER_LOCATIONS: 'characterLocations',

  // Hangout
  HANGOUT_WITH: 'hangoutWith',
  HANGOUT_LOCATION: 'hangoutLocation',
  HANGOUT_SCENARIO: 'hangoutScenario',
  HANGOUT_TYPE: 'hangoutType',
  HANGOUT_DESCRIPTION: 'hangoutDescription',
  CURRENT_LOCATION: 'currentLocation',
  CURRENT_SUBLOCATION: 'currentSubLocation',
  HEART_GENERATION_ENABLED: 'heartGenerationEnabled',
  BODY_TYPE: 'bodyType',

  // Conversation
  HANGOUT_HISTORY: 'hangout:history',
  OPENING_GREETING: 'hangout:openingGreeting',

  // Cache
  LAST_CACHE_RESET: 'lastCacheReset',
  CHAR_CACHE: 'charCache',
  COMING_SOON_CACHE: 'comingSoonCache',

  TIME_SLOT: 'timeSlot',

  // Static data
  STATIC_VERSIONS: 'static_versions',
  STATIC_DATA_PREFIX: 'static_data_',

  // Check-in
  LAST_CHECK_IN: 'lastCheckIn',
  CHECK_IN_STREAK: 'checkInStreak',
  LAST_DAILY_REWARD: 'lastDailyRewardClaim'
};

// Core helpers
const safeJSONParse = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); }
  catch { return value; }
};

const safeJSONStringify = (value) => {
  try { return JSON.stringify(value); }
  catch { return null; }
};

export const isPersistableConversationId = (conversationId) => {
  if (typeof conversationId !== 'string') return false;
  const normalized = conversationId.trim();
  if (!normalized) return false;
  if (normalized === 'active' || normalized === 'active') return false;
  if (normalized.startsWith('pending-') || normalized.startsWith('pending-')) return false;
  return true;
};

const isSystemMessage = (msg) => {
  return msg.role === 'system' || msg.isGoalChange || msg.isCharacterChange ||
    msg.isOutfitChange || msg.isLocationChange || msg.isTimeChange ||
    msg.isSystemNotification || (msg.metadata && (
      msg.metadata.goalChange || msg.metadata.characterChange ||
      msg.metadata.outfitChange || msg.metadata.locationChange ||
      msg.metadata.timeChange || msg.metadata.isSystemMessage));
};

// Storage utility that falls back to sessionStorage when localStorage is unavailable
// (e.g., Safari incognito mode)
const getStorageMechanism = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return localStorage;
  } catch (e) {
    // localStorage not available, fall back to sessionStorage
    return sessionStorage;
  }
};

const storageMechanism = getStorageMechanism();

const storage = {
  // Basic operations
  get(key) { return safeJSONParse(storageMechanism.getItem(key)); },
  set(key, value) { storageMechanism.setItem(key, JSON.stringify(value)); },
  remove(key) { storageMechanism.removeItem(key); },
  clear() { storageMechanism.clear(); },

  // JSON operations (legacy compatibility)
  getJSON(key) { return this.get(key); },
  setJSON(key, value) {
    const json = safeJSONStringify(value);
    if (json) storageMechanism.setItem(key, json);
  },
  updateJson(key, propertyKey, value) {
    const currentData = this.getJSON(key) || {};
    currentData[propertyKey] = value;
    this.setJSON(key, currentData);
  },

  // Token
  getToken() { return this.get(STORAGE_KEYS.TOKEN); },

  // Language
  getLanguage() { return this.get(STORAGE_KEYS.SELECTED_LANGUAGE) || 'en'; },
  setLanguage(lang) { this.set(STORAGE_KEYS.SELECTED_LANGUAGE, lang); },

  // Check-in operations
  getCheckInInfo() {
    const lastCheckIn = this.get(STORAGE_KEYS.LAST_CHECK_IN);
    return {
      lastCheckIn: lastCheckIn ? new Date(lastCheckIn) : null,
      streak: parseInt(this.get(STORAGE_KEYS.CHECK_IN_STREAK)) || 0,
      coins: parseInt(this.get(STORAGE_KEYS.COINS)) || 0
    };
  },

  setCheckInInfo({ lastCheckIn, streak, coins }) {
    if (lastCheckIn) this.set(STORAGE_KEYS.LAST_CHECK_IN, lastCheckIn.toISOString());
    if (streak !== undefined) this.set(STORAGE_KEYS.CHECK_IN_STREAK, streak.toString());
    if (coins !== undefined) this.set(STORAGE_KEYS.COINS, coins.toString());
  },

  // Hangout operations
  getHangoutInfo() {
    return {
      character: this.get(STORAGE_KEYS.HANGOUT_WITH),
      location: this.get(STORAGE_KEYS.HANGOUT_LOCATION),
      scenario: this.get(STORAGE_KEYS.HANGOUT_SCENARIO),
      type: this.get(STORAGE_KEYS.HANGOUT_TYPE),
      description: this.get(STORAGE_KEYS.HANGOUT_DESCRIPTION)
    };
  },

  setHangoutInfo({ character, location, scenario = '', type = 'regular', description = '' }) {
    this.set(STORAGE_KEYS.HANGOUT_WITH, character);
    this.set(STORAGE_KEYS.HANGOUT_LOCATION, location);
    this.set(STORAGE_KEYS.HANGOUT_SCENARIO, scenario);
    this.set(STORAGE_KEYS.HANGOUT_TYPE, type);
    this.set(STORAGE_KEYS.HANGOUT_DESCRIPTION, description);
  },

  // Message operations (single active conversation backed by hangout:history)

  clearOpeningGreetingMessages() {
    this.remove(STORAGE_KEYS.OPENING_GREETING);
  },

  getConversationMessages(conversationId) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (!this._isConversationContextMatch(normalizedConversationId)) return [];
    return this._readHistoryMessages();
  },

  getMainPathMessages(conversationId) {
    return this.getConversationMessages(conversationId);
  },

  setConversationMessages(conversationId, messages, metadata = {}) {
    return this.replaceConversationMessagesAtomic(conversationId, messages, {
      metadata,
      reason: 'set-conversation-messages'
    });
  },

  // Replace existing conversation messages and branches entirely
  replaceConversationMessages(conversationId, messages, metadata = {}) {
    return this.replaceConversationMessagesAtomic(conversationId, messages, {
      metadata,
      reason: 'replace-conversation-messages'
    });
  },

  replaceConversationMessagesAtomic(conversationId, messages, options = {}) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (!this._isConversationContextMatch(normalizedConversationId)) return false;
    this._ensureConversationContext(normalizedConversationId);

    const safeMessages = Array.isArray(messages) ? messages : [];
    /** @type {{ metadata?: any, reason?: string, enforceAllOrNothing?: boolean }} */
    const safeOptions = options && typeof options === 'object' ? options : {};
    const metadata = safeOptions.metadata ?? {};
    const reason = typeof safeOptions.reason === 'string' ? safeOptions.reason : 'unknown';
    const enforceAllOrNothing = safeOptions.enforceAllOrNothing === true;

    const existingMessages = this._readHistoryMessages();
    const normalizedMessages = safeMessages.map((message, index) =>
      this._normalizeConversationMessage({
        message,
        index,
        metadata,
        conversationId: normalizedConversationId
      })
    );

    const isPartialTrimBlocked = enforceAllOrNothing
      && existingMessages.length > 0
      && normalizedMessages.length > 0
      && normalizedMessages.length < existingMessages.length;
    if (isPartialTrimBlocked) {
      console.warn('[Hangout][History] Blocked partial transcript trim', {
        reason,
        conversationId: normalizedConversationId || null,
        previousCount: existingMessages.length,
        nextCount: normalizedMessages.length
      });
      return false;
    }

    this.set(STORAGE_KEYS.HANGOUT_HISTORY, normalizedMessages);
    console.log('[Hangout][History] Replaced transcript', {
      reason,
      conversationId: normalizedConversationId || null,
      previousCount: existingMessages.length,
      nextCount: normalizedMessages.length,
      enforceAllOrNothing
    });
    return true;
  },

  // Computed conversation metadata (replaces stored metadata)
  getConversationMetadata(conversationId) {
    return this._computeConversationMetadata(conversationId);
  },

  // Compute metadata on-demand from existing systems
  _computeConversationMetadata(conversationId) {
    try {
      // Get data from existing systems
      const messages = this.getConversationMessages(conversationId);
      const messageData = this._getMessageData(conversationId);
      const config = this._getCurrentHangoutConfig();

      if (!messages || messages.length === 0) {
        return null; // No conversation exists
      }

      // Extract basic info
      const lastMessage = messages[messages.length - 1];
      const firstMessage = messages[0];

      // Get character info from config or messages
      let characterId, characterName, allCharacters = null;
      let isMultiCharacter = false;

      if (config?.conversationId === conversationId) {
        // Use current config if it matches this conversation
        characterId = config.allCharacters?.[0]?.id;
        allCharacters = config.allCharacters;
        isMultiCharacter = config.allCharacters?.length > 1;
        characterName = config.allCharacters?.[0]?.name || `Character ${characterId}`;
      } else {
        // Extract from messages
        const assistantMessages = messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length > 0) {
          characterId = assistantMessages[0].characterId;
          characterName = assistantMessages[0].characterName || `Character ${characterId}`;

          // Check for multi-character conversation
          const uniqueCharacters = new Set(assistantMessages.map(msg => msg.characterId).filter(Boolean));
          isMultiCharacter = uniqueCharacters.size > 1;

          if (isMultiCharacter) {
            allCharacters = Array.from(uniqueCharacters).map(id => {
              const charMsg = assistantMessages.find(msg => msg.characterId === id);
              return {
                id,
                name: charMsg?.characterName || `Character ${id}`,
                outfitId: charMsg?.outfitId || 0
              };
            });
          }
        }
      }

      // Get scenario info from config or messages
      let scenarioId = 'unknown', scenarioName = 'Unknown Scenario', scenarioDescription = null;
      if (config?.conversationId === conversationId) {
        scenarioId = getScenarioIdFromHangoutConfig(config) || 'unknown';
        scenarioName = getScenarioNameFromHangoutConfig(config, 'Hangout Session');
        scenarioDescription = getScenarioDescriptionFromHangoutConfig(config, null);
      }

      // Get location info from latest message with location data
      let backgroundId = 0, backgroundUrl = null, locationName = 'Default Room', timeOfDay = 'night';
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.backgroundId !== undefined || msg.timeOfDay !== undefined) {
          backgroundId = msg.backgroundId || 0;
          backgroundUrl = msg.backgroundUrl || null;
          timeOfDay = msg.timeOfDay || 'night';
          break;
        }
      }

      let lastSpriteState = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant' && messages[i].actionId !== undefined && messages[i].actionId !== null) {
          lastSpriteState = String(messages[i].actionId);
          break;
        }
      }

      // Get outfit from latest character data
      let outfitId = 0;
      if (allCharacters?.length > 0) {
        outfitId = allCharacters[0].outfitId || 0;
      } else if (config?.conversationId === conversationId) {
        outfitId = config.allCharacters?.[0]?.outfitId || 0;
      }

      return {
        conversationId,
        characterId: characterId || '',
        characterName: characterName || `Character ${characterId}`,
        scenarioId,
        scenarioName,
        scenarioDescription,
        backgroundId,
        backgroundUrl,
        locationName,
        locationId: backgroundId,
        timeOfDay,
        lastMessage: lastMessage?.content?.substring(0, 100) || '',
        messageCount: messages.length,
        lastSpriteState,
        outfitId,
        isMultiCharacter,
        characterCount: allCharacters?.length || 1,
        allCharacters,
        lastUpdated: messageData?.lastUpdated || Date.now(),
        createdAt: firstMessage?.timestamp || firstMessage?.createdAt || Date.now(),
        source: 'computed'
      };
    } catch (error) {
      console.error('Error computing conversation metadata:', error);
      return null;
    }
  },

  // Helper to get current hangout config
  _getCurrentHangoutConfig() {
    try {
      return JSON.parse(storageMechanism.getItem('currentHangoutConfig') || '{}');
    } catch {
      return {};
    }
  },

  // Legacy compatibility - no-op for setConversationMetadata
  setConversationMetadata(conversationId, metadata) {
    // Metadata is now computed on-demand, no storage needed
    return true;
  },

  getRecentConversationsForCharacter(characterId, limit = 20) {
    const allConversationIds = this.getAllConversationIds();
    const characterConversations = [];

    for (const conversationId of allConversationIds) {
      const metadata = this.getConversationMetadata(conversationId);
      if (!metadata) continue;

      const characterParticipated = this._checkCharacterParticipation(metadata, characterId);
      if (!characterParticipated) continue;

      const characterInConversation = metadata.allCharacters?.find(char =>
        String(char.id) === String(characterId));

      characterConversations.push({
        conversationId,
        characterId: characterId,
        characterName: characterInConversation?.name || metadata.characterName || `Character ${characterId}`,
        scenarioId: metadata.scenarioId || 'unknown',
        scenarioName: metadata.scenarioName || 'Unknown Scenario',
        backgroundId: metadata.backgroundId || metadata.locationId || 0,
        backgroundUrl: metadata.backgroundUrl || null,
        locationName: metadata.locationName || 'Default Room',
        outfitId: characterInConversation?.outfitId || metadata.outfitId || 0,
        locationId: metadata.locationId || metadata.backgroundId || 0,
        timeOfDay: metadata.timeOfDay || 'night',
        scenarioDescription: metadata.scenarioDescription || null,
        lastMessage: metadata.lastMessage || '',
        lastUpdated: metadata.lastUpdated || Date.now(),
        messageCount: metadata.messageCount || 0,
        createdAt: metadata.createdAt || metadata.lastUpdated || Date.now(),
        lastSpriteState: metadata.lastSpriteState || metadata.lastEmotion || null,
        isMultiCharacter: metadata.isMultiCharacter || (metadata.allCharacters?.length > 1),
        characterCount: metadata.characterCount || metadata.allCharacters?.length || 1,
        allCharacters: metadata.allCharacters || null,
        isPrimaryCharacter: String(metadata.characterId) === String(characterId),
        source: 'localStorage'
      });
    }

    // Sort by lastUpdated and apply limit
    return characterConversations
      .sort((a, b) => {
        const aTime = typeof a.lastUpdated === 'number' ? a.lastUpdated : new Date(a.lastUpdated).getTime();
        const bTime = typeof b.lastUpdated === 'number' ? b.lastUpdated : new Date(b.lastUpdated).getTime();
        return bTime - aTime;
      })
      .slice(0, limit);
  },

  async getCompleteConversation(conversationId) {
    const tokenManager = typeof window !== 'undefined' ? /** @type {any} */ (window).tokenManager : null;
    const authToken = tokenManager && typeof tokenManager.getValidAccessToken === 'function'
      ? await tokenManager.getValidAccessToken()
      : null;
    if (!authToken) return null;

    try {
      const language = encodeURIComponent(this.getLanguage() || 'en');
      const isPrivateScenario = this._getCurrentHangoutConfig()?.isPrivateScenario === true;
      const data = await fetchCache.fetchJSON(
        GROK_WORKER_URL + '/v1/hangout/conversation?conversationId=' + encodeURIComponent(conversationId) + '&language=' + language + '&isPrivateScenario=' + (isPrivateScenario ? 'true' : 'false'),
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!data || !Array.isArray(data.messages)) return null;

        const existingConfig = this._getCurrentHangoutConfig();
        const messages = normalizeRemoteConversationMessages(
          data.messages,
          existingConfig?.allCharacters
        );

        let lastLocId = null;
        let lastTimeOfDay = null;
        let lastTime = null;
        const charMap = new Map();
        for (const m of messages) {
          if (m.locationId != null && Number.isFinite(Number(m.locationId))) {
            lastLocId = Number(m.locationId);
          }
          if (m.time != null && Number.isFinite(Number(m.time))) {
            lastTime = Number(m.time);
            lastTimeOfDay = militaryToTimeOfDaySlot(lastTime) || lastTimeOfDay;
          }
          if (m.characterId) {
            const charId = String(m.characterId);
            const prev = charMap.get(charId) || {};
            const messageName = typeof m.characterName === 'string' ? m.characterName.trim() : '';
            const prevName = typeof prev.name === 'string' ? prev.name.trim() : '';
            const resolvedName = messageName || prevName || null;
            charMap.set(charId, {
              id: charId,
              name: resolvedName,
              outfitId: String(m.outfitId || prev.outfitId || 0),
              actionId: m.actionId ?? prev.actionId ?? null,
              actionName: m.actionName || prev.actionName || null,
              goal: m.goal || prev.goal || null
            });
          }
        }

        const persistMetadata = {
          scenarioId: data.scenarioId || null,
          locationId: lastLocId || 0,
          timeOfDay: lastTimeOfDay || 'night',
          currentTimeMilitary: lastTime,
          activeCharacters: Array.from(charMap.values())
        };
        const persisted = this.replaceConversationMessagesAtomic(conversationId, messages, {
          reason: 'backend-conversation-hydration',
          enforceAllOrNothing: true,
          metadata: persistMetadata
        });
        if (!persisted) {
          this.set(STORAGE_KEYS.HANGOUT_HISTORY, messages);
          console.warn('[Hangout][History] Atomic persist blocked; forced hangout:history override', {
            reason: 'backend-conversation-hydration',
            conversationId,
            messageCount: messages.length
          });
        }

        // Update currentHangoutConfig with accumulated state
        try {
          const { storageManager } = await import('../../core/storage/index.js');
          const cfg = (await storageManager.getItem('currentHangoutConfig')) || {};
          const existingChars = Array.isArray(cfg.allCharacters) ? cfg.allCharacters : [];
          const merged = new Map(existingChars.map(c => [String(c.id), c]));
          Array.from(charMap.values()).forEach(ch => {
            const prev = merged.get(ch.id) || {};
            const prevName = typeof prev.name === 'string' ? prev.name : null;
            const messageName = typeof ch.name === 'string' ? ch.name : null;
            const preferPrev = prevName && !isPlaceholderCharacterName(prevName, ch.id);
            const preferMessage = messageName && !isPlaceholderCharacterName(messageName, ch.id);
            merged.set(ch.id, {
              id: ch.id,
              name: preferPrev ? prevName : (preferMessage ? messageName : (prevName || `Character ${ch.id}`)),
              outfitId: ch.outfitId || prev.outfitId || '0',
              actionId: ch.actionId ?? prev.actionId ?? null,
              actionName: ch.actionName || prev.actionName || null,
              goal: ch.goal || prev.goal || null
            });
          });
          cfg.allCharacters = Array.from(merged.values());
          if (lastLocId !== null) cfg.locId = String(lastLocId);
          if (lastTimeOfDay) cfg.timeOfDay = lastTimeOfDay;
          if (lastTime != null) {
            const hours = Math.floor(lastTime / 100);
            const mins = lastTime % 100;
            cfg.currentTime = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
          }
          if (data.scenarioId != null) cfg.scenarioId = String(data.scenarioId);
          if (conversationId) cfg.conversationId = conversationId;
          await storageManager.setItem('currentHangoutConfig', cfg);
        } catch { }
        return {
          messages,
          conversationState: null,
          messageCount: (data.meta && data.meta.messageCount) || messages.length,
          source: 'backend'
        };
    } catch (error) {
      console.error('Storage: Error fetching conversation from backend:', error);
      return null;
    }
  },

  getAllConversationIds() {
    const activeConversationId = this._getActiveConversationId();
    const historyMessages = this._readHistoryMessages();
    if (activeConversationId && historyMessages.length > 0) {
      return [activeConversationId];
    }
    return [];
  },

  removeConversation(conversationId) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (this._isConversationContextMatch(normalizedConversationId)) {
      const previousCount = this._readHistoryMessages().length;
      this.remove(STORAGE_KEYS.HANGOUT_HISTORY);
      this.clearOpeningGreetingMessages();
      this.remove('hangout:chatMemory');
      this.remove('hangout:characterInfoOverrides');
      console.log('[Hangout][History] Removed conversation transcript', {
        reason: 'remove-conversation',
        conversationId: normalizedConversationId || null,
        previousCount
      });
    }
  },

  clearAllConversations() {
    const conversationIds = this.getAllConversationIds();
    const previousCount = this._readHistoryMessages().length;
    this.remove(STORAGE_KEYS.HANGOUT_HISTORY);
    this.clearOpeningGreetingMessages();
    this.remove('hangout:chatMemory');
    this.remove('hangout:characterInfoOverrides');
    console.log('[Hangout][History] Cleared all conversations', {
      reason: 'clear-all-conversations',
      previousCount,
      conversationCount: conversationIds.length
    });
    return conversationIds.length;
  },

  addMessage(conversationId, message, metadata = {}) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (!this._isConversationContextMatch(normalizedConversationId)) {
      return {
        success: false,
        messagePath: null,
        branchName: 'main',
        isNewBranch: false
      };
    }
    this._ensureConversationContext(normalizedConversationId);

    const historyMessages = this._readHistoryMessages();
    const nextIndex = historyMessages.length;
    const normalizedMessage = this._normalizeConversationMessage({
      message,
      index: nextIndex,
      metadata,
      conversationId: normalizedConversationId
    });
    const nextMessages = [...historyMessages, normalizedMessage];
    const persisted = this.replaceConversationMessagesAtomic(normalizedConversationId, nextMessages, {
      reason: 'add-message',
      metadata
    });
    if (!persisted) {
      return {
        success: false,
        messagePath: null,
        branchName: 'main',
        isNewBranch: false
      };
    }

    return {
      success: true,
      messagePath: normalizedMessage.path,
      branchName: 'main',
      isNewBranch: false
    };
  },

  updateMessageContent(conversationId, messagePath, newContent, extraUpdates = {}) {
    if (!conversationId || !messagePath) return false;

    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (!this._isConversationContextMatch(normalizedConversationId)) return false;

    const historyMessages = this._readHistoryMessages();
    const messageIndex = historyMessages.findIndex(msg =>
      String(msg?.path || '') === String(messagePath)
    );
    if (messageIndex < 0) return false;

    historyMessages[messageIndex] = {
      ...historyMessages[messageIndex],
      content: newContent,
      ...extraUpdates
    };
    return this.replaceConversationMessagesAtomic(normalizedConversationId, historyMessages, {
      reason: 'update-message-content'
    });
  },

  getActiveBranchMessages(conversationId) {
    return this.getConversationMessages(conversationId);
  },

  // Private helper methods
  _getMessageKey(conversationId) {
    void conversationId;
    return STORAGE_KEYS.HANGOUT_HISTORY;
  },

  _getMessageData(conversationId) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (!this._isConversationContextMatch(normalizedConversationId)) return null;
    const messages = this._readHistoryMessages();
    if (messages.length === 0) return null;
    return this._buildMessageDataFromHistory(messages, normalizedConversationId);
  },

  _initMessageData() {
    return {
      messagePaths: {},
      activeBranch: 'main',
      branchPaths: { main: [] }
    };
  },

  _normalizeConversationId(conversationId) {
    if (conversationId === undefined || conversationId === null) return '';
    const normalizedConversationId = String(conversationId).trim();
    return isPersistableConversationId(normalizedConversationId) ? normalizedConversationId : '';
  },

  _getActiveConversationId() {
    const config = this._getCurrentHangoutConfig();
    const conversationId = this._normalizeConversationId(config?.conversationId);
    return conversationId || '';
  },

  _isConversationContextMatch(conversationId) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    const activeConversationId = this._getActiveConversationId();
    if (!normalizedConversationId) return true;
    if (!activeConversationId) return true;
    return activeConversationId === normalizedConversationId;
  },

  _ensureConversationContext(conversationId) {
    const normalizedConversationId = this._normalizeConversationId(conversationId);
    if (!normalizedConversationId) return;
    const config = this._getCurrentHangoutConfig();
    const currentConversationId = this._normalizeConversationId(config?.conversationId);
    if (currentConversationId === normalizedConversationId) return;
    this.set('currentHangoutConfig', {
      ...config,
      conversationId: normalizedConversationId
    });
  },

  _readHistoryMessages() {
    const historyMessages = this.get(STORAGE_KEYS.HANGOUT_HISTORY);
    return Array.isArray(historyMessages) ? historyMessages : [];
  },

  _normalizeCharacterId(characterId) {
    if (characterId === undefined || characterId === null || characterId === '') return null;
    const normalizedCharacterId = String(characterId).trim();
    return normalizedCharacterId || null;
  },

  _getCharacterNameFromConfig(characterId) {
    const normalizedCharacterId = this._normalizeCharacterId(characterId);
    if (!normalizedCharacterId) return null;
    const config = this._getCurrentHangoutConfig();
    const allCharacters = Array.isArray(config?.allCharacters) ? config.allCharacters : [];
    const matchedCharacter = allCharacters.find((character) =>
      this._normalizeCharacterId(character?.id ?? character?.characterId) === normalizedCharacterId
    );
    const resolvedName = matchedCharacter?.name;
    return typeof resolvedName === 'string' && resolvedName.trim() ? resolvedName.trim() : null;
  },

  _parseMilitaryTime(value) {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 2359) return null;
    if ((parsed % 100) >= 60 || Math.floor(parsed / 100) > 23) return null;
    return parsed;
  },

  _parseLocationId(value) {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return parsed;
  },

  _normalizeConversationMessage({ message, index, metadata, conversationId }) {
    const safeMessage = message && typeof message === 'object' ? message : {};
    const rawPath = safeMessage.path != null ? String(safeMessage.path) : '';
    const isGreeting = safeMessage.isGreeting === true || safeMessage.isInitialGreeting === true;
    const normalizedCharacterId = this._normalizeCharacterId(safeMessage.characterId);
    const path = rawPath || (index + 1).toString();
    const resolvedCharacterName = this._getCharacterNameFromConfig(normalizedCharacterId)
      || (typeof safeMessage.characterName === 'string' ? safeMessage.characterName.trim() : null)
      || null;
    const parsedActionId = this._toActionId(
      safeMessage.actionId
      ?? safeMessage.action_id
      ?? null
    );
    const actionNameRaw = typeof safeMessage.actionName === 'string'
      ? safeMessage.actionName
      : (typeof safeMessage.action_name === 'string' ? safeMessage.action_name : '');
    const actionName = actionNameRaw.trim() || null;
    const normalizedOutfitId = safeMessage.outfitId
      ?? safeMessage.outfit_id
      ?? null;
    const locationId = this._parseLocationId(
      safeMessage.locationId ?? safeMessage.location_id
    );
    const time = this._parseMilitaryTime(safeMessage.time);
    void metadata;
    void conversationId;

    return {
      path,
      role: safeMessage.role,
      content: safeMessage.content,
      characterId: normalizedCharacterId,
      characterName: resolvedCharacterName,
      actionId: parsedActionId,
      ...(actionName ? { actionName } : {}),
      outfitId: normalizedOutfitId,
      isVideo: safeMessage.isVideo === true,
      createdAt: safeMessage.createdAt || safeMessage.timestamp || Date.now(),
      branchName: this._getBranchName(path),
      parentPath: this._getParentPath(path),
      isMainPath: !String(path).includes('.'),
      ...(locationId != null ? { locationId } : {}),
      ...(time != null ? { time } : {}),
      ...(isGreeting ? { isGreeting: true, isInitialGreeting: true } : {}),
      ...(safeMessage.isJoinMessage === true ? { isJoinMessage: true } : {}),
      ...(safeMessage.isLeaveMessage === true ? { isLeaveMessage: true } : {}),
      ...(safeMessage.isCharacterChange === true ? { isCharacterChange: true } : {}),
      ...(safeMessage.isGoalChange === true ? { isGoalChange: true } : {}),
      ...(safeMessage.isLocationChange === true ? { isLocationChange: true } : {}),
      ...(safeMessage.isOutfitChange === true ? { isOutfitChange: true } : {}),
      ...(safeMessage.persistedRemote === true ? { persistedRemote: true } : {})
    };
  },

  _buildMessageDataFromHistory(messages, conversationId) {
    const messageData = this._initMessageData();
    const normalizedMessages = messages.map((message, index) => this._normalizeConversationMessage({
      message,
      index,
      metadata: {},
      conversationId
    }));

    normalizedMessages.forEach((message) => {
      const branchName = message.branchName || 'main';
      if (!messageData.branchPaths[branchName]) {
        messageData.branchPaths[branchName] = [];
      }
      messageData.messagePaths[message.path] = message;
      messageData.branchPaths[branchName].push(message.path);
    });

    const latestCreatedAt = normalizedMessages.reduce((latest, message) => {
      const createdAt = Number(message.createdAt) || 0;
      return createdAt > latest ? createdAt : latest;
    }, 0);

    return {
      ...messageData,
      conversationId: conversationId || this._getActiveConversationId(),
      messageCount: normalizedMessages.length,
      lastUpdated: latestCreatedAt || Date.now(),
      metadata: {}
    };
  },

  _getBranchName(path) {
    return path.includes('.') ? `branch_${path.split('.').slice(0, -1).join('_')}` : 'main';
  },

  _getMessageCharacters(message, metadata) {
    return message.characterId ? [{
      id: message.characterId,
      outfitId: message.outfitId || 0,
      actionId: message.actionId ?? null,
      actionName: message.actionName || null,
      name: message.characterName || 'Character'
    }] : [];
  },

  _groupMessagesByRootPath(messagePaths) {
    const pathGroups = {};
    Object.values(messagePaths).forEach(msg => {
      const rootPath = msg.path.split('.')[0];
      if (!pathGroups[rootPath]) pathGroups[rootPath] = [];
      pathGroups[rootPath].push(msg);
    });
    return pathGroups;
  },

  _compareMessagePaths(a, b) {
    const aDepth = a.path.split('.').length;
    const bDepth = b.path.split('.').length;
    if (aDepth !== bDepth) return aDepth - bDepth;
    return a.path.localeCompare(b.path);
  },

  _toActionId(value) {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number.parseInt(String(value), 10);
    return Number.isNaN(parsed) ? null : parsed;
  },

  _getParentPath(path) {
    const parts = path.split('.');
    return parts.length <= 1 ? null : parts.slice(0, -1).join('.');
  },

  _checkCharacterParticipation(metadata, characterId) {
    return String(metadata.characterId) === String(characterId) ||
      (metadata.allCharacters?.some(char => String(char.id) === String(characterId)));
  },

  _isPathBeforeOrEqual(pathA, pathB) {
    // Check if pathA comes before or is equal to pathB in conversation order
    if (!pathA || !pathB) return false;

    const parsePathNumber = (path) => {
      const parts = path.split('.');
      return parseInt(parts[0]) || 0;
    };

    const numA = parsePathNumber(pathA);
    const numB = parsePathNumber(pathB);

    return numA <= numB;
  },

};

export const clearPersistedClientData = async () => {
  let languagePref = null;
  try {
    languagePref = storage.get(STORAGE_KEYS.SELECTED_LANGUAGE);
  } catch {}

  try {
    await storageManager.clear();
  } catch (error) {
    console.warn('clearPersistedClientData: storageManager.clear failed:', error);
    try { storage.clear(); } catch {}
  }

  if (storageMechanism === localStorage) {
    try { sessionStorage.clear(); } catch {}
  }

  if (languagePref) {
    try { storage.set(STORAGE_KEYS.SELECTED_LANGUAGE, languagePref); } catch {}
  }
};

export { storage, STORAGE_KEYS, storageMechanism }; 
